import { Component } from '@theme/component';

/**
 * @typedef {object} CartBatchCountdownRefs
 * @property {HTMLElement} text - The countdown sentence.
 */

/**
 * Ticks down to the server-computed weekly batch cutoff (`data-deadline`, epoch seconds) and hides
 * itself once it passes. Section re-renders replace the markup; updatedCallback restarts the clock.
 *
 * @extends Component<CartBatchCountdownRefs>
 */
class CartBatchCountdown extends Component {
  requiredRefs = ['text'];

  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
  }

  #start() {
    window.clearInterval(this.#interval);
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  #tick() {
    const deadline = Number(this.dataset.deadline) * 1000;
    const template = this.dataset.template;
    const remaining = deadline - Date.now();

    if (!deadline || !template || remaining <= 0) {
      this.hidden = true;
      window.clearInterval(this.#interval);
      return;
    }

    const total = Math.floor(remaining / 1000);
    const days = Math.floor(total / 86400);
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    const clock = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
    const label = days > 0 ? `${days}d ${clock}` : clock;

    const time = document.createElement('time');
    time.dateTime = `PT${total}S`;
    time.textContent = label;

    // The template is trusted, server-rendered translation markup; only the time is injected.
    const [before, after = ''] = template.split('[time]');
    this.refs.text.innerHTML = `${before}${time.outerHTML}${after}`;
  }
}

if (!customElements.get('cart-batch-countdown-component')) {
  customElements.define('cart-batch-countdown-component', CartBatchCountdown);
}
